/**
 * Tenant List Component
 * 
 * Displays tenants as cards with edit functionality.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchAllTenants,
  fetchAllFactories,
  createTenant,
  updateTenant,
} from '../api';
import { useToast } from '@/shared/hooks/use-toast';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';
import { Factory, Plus, Pencil, Loader2, Building2, Languages } from 'lucide-react';
import { getLanguageInfo } from '@/shared/config/languages';
import { useLanguage } from '@/core/LanguageContext';
import { TenantEditDialog } from './TenantEditDialog';
import type { Tenant, CreateTenantInput, UpdateTenantInput, TenantFactory } from '../types';

export function TenantList() {
  const { t } = useLanguage();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);

  const { data: tenants = [], isLoading: tenantsLoading } = useQuery({
    queryKey: ['superadmin', 'tenants'],
    queryFn: fetchAllTenants,
  });

  const { data: factories = [] } = useQuery({
    queryKey: ['superadmin', 'factories'],
    queryFn: fetchAllFactories,
  });

  // Group factories by tenant
  const factoriesByTenant = factories.reduce(
    (acc, factory) => {
      if (!acc[factory.tenant_id]) acc[factory.tenant_id] = [];
      acc[factory.tenant_id].push(factory);
      return acc;
    },
    {} as Record<string, TenantFactory[]>
  );

  const createMutation = useMutation({
    mutationFn: (data: CreateTenantInput) => createTenant(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'tenants'] });
      toast({ title: t('superadmin.tenants.createSuccess', 'Tenant created successfully') });
      setIsDialogOpen(false);
      setEditingTenant(null);
    },
    onError: (error: Error) => {
      toast({
        title: t('superadmin.tenants.createError', 'Failed to create tenant'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTenantInput }) => updateTenant(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'tenants'] });
      toast({ title: t('superadmin.tenants.updateSuccess', 'Tenant updated successfully') });
      setIsDialogOpen(false);
      setEditingTenant(null);
    },
    onError: (error: Error) => {
      toast({
        title: t('superadmin.tenants.updateError', 'Failed to update tenant'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleSave = (data: CreateTenantInput | UpdateTenantInput) => {
    if (editingTenant) {
      updateMutation.mutate({ id: editingTenant.id, data });
    } else {
      createMutation.mutate(data as CreateTenantInput);
    }
  };

  const openCreateDialog = () => {
    setEditingTenant(null);
    setIsDialogOpen(true);
  };

  const openEditDialog = (tenant: Tenant) => {
    setEditingTenant(tenant);
    setIsDialogOpen(true);
  };

  if (tenantsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">
            {t('superadmin.tenants.title', 'Tenants')}
          </h2>
          <Badge variant="secondary">{tenants.length}</Badge>
        </div>
        <Button variant="industrial" onClick={openCreateDialog}>
          <Plus className="h-4 w-4 mr-2" />
          {t('superadmin.tenants.addButton', 'Add Tenant')}
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">
        {t(
          'superadmin.tenants.description',
          'Manage organization tenants and their settings.'
        )}
      </p>

      {tenants.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Factory className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">
              {t(
                'superadmin.tenants.empty',
                'No tenants created yet. Add your first tenant to get started.'
              )}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {tenants.map((tenant) => {
            const secondaryLang = tenant.settings?.secondary_language;
            const tenantFactories = factoriesByTenant[tenant.id] || [];

            return (
              <Card key={tenant.id} className="relative">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Factory className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{tenant.name}</CardTitle>
                        <Badge variant="secondary" className="mt-1">
                          {tenant.code}
                        </Badge>
                      </div>
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => openEditDialog(tenant)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <Badge variant={tenant.is_active ? 'default' : 'secondary'}>
                      {tenant.is_active
                        ? t('superadmin.tenants.active', 'Active')
                        : t('superadmin.tenants.inactive', 'Inactive')}
                    </Badge>
                    <div className="flex gap-2 flex-wrap">
                      {secondaryLang && (
                        <Badge variant="outline" className="text-xs gap-1">
                          <Languages className="h-3 w-3" />
                          {getLanguageInfo(secondaryLang)?.flag}{' '}
                          {getLanguageInfo(secondaryLang)?.name}
                        </Badge>
                      )}
                      {tenant.microsoft_tenant_id && (
                        <Badge variant="outline" className="text-xs">
                          MS Tenant
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 space-y-2">
                    <p className="text-xs text-muted-foreground">
                      {t('superadmin.tenants.factories', 'Factories')} ({tenantFactories.length})
                    </p>
                    {tenantFactories.length === 0 ? (
                      <p className="text-xs text-muted-foreground">
                        {t('superadmin.tenants.noFactories', 'No factories assigned')}
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {tenantFactories.map((factory) => (
                          <Badge key={factory.id} variant="outline" className="text-xs">
                            {factory.name} ({factory.code})
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <TenantEditDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        tenant={editingTenant}
        onSave={handleSave}
        isPending={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  );
}
